import type { UserRole } from '@/entities/session/types'

/** Relacionamento familiar para perfil paciente. */
export type FamilyRelationship = 'self' | 'child' | 'parent' | 'spouse' | 'other'

/**
 * Retorna o título da página de pacientes conforme o perfil.
 * - patient: "Meus Dados"
 * - doctor/admin: "Pacientes"
 */
export function getPatientsPageTitle(role: UserRole): string {
  return role === 'patient' ? 'Meus Dados' : 'Pacientes'
}

/**
 * Retorna o subtítulo da página de pacientes conforme o perfil.
 * Breve descrição do objetivo e da navegação.
 */
export function getPatientsPageSubtitle(role: UserRole): string {
  return role === 'patient'
    ? 'Seus dados e de familiares. Toque no card para abrir o prontuário.'
    : 'Cadastre pacientes e acesse prontuários pelo card.'
}

/**
 * Retorna o label do botão de criar paciente/familiar conforme o perfil.
 * - patient: "Novo familiar"
 * - doctor/admin: "Novo Paciente"
 */
export function getPatientCreateButtonLabel(role: UserRole): string {
  return role === 'patient' ? 'Novo familiar' : 'Novo Paciente'
}

/**
 * Indica se o formulário de paciente deve incluir tags (apenas médico/admin).
 * Perfil paciente não edita tags.
 */
export function shouldIncludeTagsInPatientForm(role: UserRole): boolean {
  return role !== 'patient'
}

/**
 * Indica se o formulário de paciente deve incluir relacionamento familiar.
 * Apenas perfil paciente usa relationship e linkRole.
 */
export function shouldIncludeRelationshipInPatientForm(role: UserRole): boolean {
  return role === 'patient'
}

/**
 * Retorna o relacionamento padrão ao criar paciente.
 * - patient com 0 pacientes: 'self' (primeiro cadastro é o próprio usuário)
 * - patient com 1+ pacientes: 'other' (adicionando familiar)
 * - doctor/admin: não usa, mas retorna 'other' como fallback
 */
export function getDefaultRelationship(
  role: UserRole,
  patientCount: number,
): FamilyRelationship {
  if (role === 'patient' && patientCount === 0) return 'self'
  return 'other'
}

/**
 * Indica se o usuário deve ver a view de paciente na página de documentos.
 * - patient: PatientView (apenas seus documentos)
 * - doctor/admin: DoctorView (gestão completa)
 */
export function shouldShowPatientViewInDocuments(role: UserRole): boolean {
  return role === 'patient'
}

/** Paciente visualiza documentos existentes, mas não cria receitas/atestados novos. */
export function canCreateClinicalDocuments(role: UserRole): boolean {
  return role !== 'patient'
}

/**
 * Indica se o usuário pode editar tags no formulário de paciente.
 */
export function canEditPatientTags(role: UserRole): boolean {
  return role !== 'patient'
}

/**
 * Indica se o perfil pode acessar o chat com IA (AskAiBar, rota /chat-ia).
 * Apenas médico e admin têm acesso; paciente não.
 */
export function canUseAiChat(role: UserRole): boolean {
  return role !== 'patient'
}
